/**
 * Where to go next, offered at the end of an answer.
 *
 * Placed after the review rather than before it, because the corrections are
 * part of the answer and these are not — they are the next turn. Quiet by
 * design: an answer you are still reading should not be competing with four
 * buttons for attention.
 */

import { ArrowUpRight } from "lucide-react";
import type { FollowUp } from "../../shared/agents.ts";
import { Button } from "./ui/button.tsx";
import { MathText } from "../markdown/MathText.tsx";

interface FollowUpsProps {
  readonly followUps: readonly FollowUp[];
  readonly onPick: (prompt: string) => void;
  /** Sending is disabled while another answer is still streaming. */
  readonly disabled?: boolean;
}

export function FollowUps({ followUps, onPick, disabled }: FollowUpsProps) {
  if (followUps.length === 0) return null;

  return (
    <nav className="mt-6 border-t pt-4 font-sans" aria-label="Suggested next questions">
      {/* h3, matching the review panel: both are top-level regions of an
          answer, and an answer is the h2. */}
      <h3 className="mb-2 text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
        Ask next
      </h3>

      <ul className="grid gap-1.5">
        {followUps.map((followUp) => (
          <li key={followUp.label} className="min-w-0">
            <Button
              variant="ghost"
              disabled={disabled}
              onClick={() => onPick(followUp.prompt)}
              title={followUp.prompt}
              className="group/followup h-auto w-full justify-between gap-3 px-3 py-2 text-left font-serif text-[0.95rem] font-normal whitespace-normal text-secondary-foreground hover:bg-muted hover:text-foreground"
            >
              <MathText
                className="min-w-0 wrap-anywhere"
                children={followUp.label}
              />
              <ArrowUpRight className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/followup:opacity-100" />
            </Button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
